import IconButton from '@components/v2/ui/IconButton/IconButton';
import { Box } from '@mui/material';
import { Pencil } from 'lucide-react';
import LangEnum from '../../../../../../lang/LangEnum';
import { CatalogProductsTestIds } from '../../../../../../utils/testIds/CatalogProducts.testIds';
import { UsedInOfflineMode } from '../../../../../offline/types/UsedInOfflineMode';
import { useTranslation } from '../../../../../translation/TranslationContext';
import CatalogBannersModal from '../CatalogBannersModal/CatalogBannersModal';
import CatalogBannerCarousel from './components/CatalogBannerCarousel';
import useCatalogBanner from './hooks/useCatalogBanner';

interface CatalogBannerProps extends UsedInOfflineMode {
  catalogId: string;
  isReadOnly: boolean;
}

const CatalogBanner = ({ catalogId, isReadOnly, isOfflineMode }: CatalogBannerProps) => {
  const { translate } = useTranslation();
  const {
    catalogName,
    bannerUrls,
    hasAnyBanner,
    bannerAspectRatio,
    canManageBanners,
    isBannersModalOpen,
    openBannersModal,
    closeBannersModal,
  } = useCatalogBanner({ catalogId, isReadOnly, isOfflineMode });

  const hasBannersForDevice = bannerUrls.length > 0;

  if (!hasBannersForDevice && !(canManageBanners && hasAnyBanner)) {
    return null;
  }

  return (
    <Box sx={{ position: 'relative' }}>
      {hasBannersForDevice && (
        <CatalogBannerCarousel
          urls={bannerUrls}
          alt={catalogName}
          aspectRatio={bannerAspectRatio}
        />
      )}

      {canManageBanners && (
        <>
          <IconButton
            icon={Pencil}
            size="medium"
            color="secondary"
            shape="round"
            testId={CatalogProductsTestIds.BANNER_EDIT}
            ariaLabel={translate(LangEnum.CATALOGS_MANAGE_BANNERS)}
            sx={{
              ...(hasBannersForDevice ? { position: 'absolute', top: 12, right: 12 } : {}),
              border: 0,
            }}
            onClick={openBannersModal}
          />

          <CatalogBannersModal
            catalogId={catalogId}
            isOpen={isBannersModalOpen}
            onClose={closeBannersModal}
          />
        </>
      )}
    </Box>
  );
};

export default CatalogBanner;
